import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientRiskItemService } from './../../../../core/services/patient-risk-item.service';
import { PatientService } from './../../../../core/services/patient.service';
import {
  RISK_CATEGORIES,
  RISK_STATUSES,
  riskCategoryLabel,
  riskLocationLabel,
  riskSeverityLabel,
  RISK_SEVERITIES,
} from './../../../../shared/components/risk-suggestion-list/risk-suggestion.model';

/**
 * WORKSTREAM L — Risk Management as a patient-file SECTION.
 *
 * Phase 1 put confirmed `PatientRiskItem` rows in the clinical timeline (D11). The user replaced
 * that placement: the items get their own collapsible section alongside Tasks, grouped by date and
 * filterable by category, with a `+` in the right sidebar that adds one by hand. L6 removes the
 * timeline half; this is the half that replaces it, and **D11 is withdrawn.**
 *
 * Structurally a copy of `patient-task-list.component.ts` — `mat-expansion-panel`,
 * `initialExpanded`/`expandedChange`, `listData` grouped by date, `listLoading`/`noResults` — so
 * that a section in this file behaves like every other section in this file.
 *
 * ── THE ONE THING THAT IS NOT A COPY: THE GROUPING KEY ───────────────────────────────────
 *
 * The date is `dateOfIssue`, NEVER `createdAt`. `dateOfIssue` is the calendar day AT THE FACILITY
 * (§5c); `createdAt` is the day someone typed it in. D13 makes hand-added items first class and the
 * add dialog's datepicker exists precisely so a doctor can backdate one — so an item entered today
 * about last month's stock-out must file under last month, or the section says the shortage
 * happened on a day it did not. This is the same rule J3 carried in the timeline, moved here.
 *
 * `dateOfIssue` is `DATEONLY` and crosses JSON as `'YYYY-MM-DD'` with no timezone. The key is read
 * off that string rather than through `new Date()`, so a browser west of UTC cannot shift the day
 * by one — the failure mode that would be invisible in this timezone and wrong in another.
 */
@Component({
  selector: 'app-patient-risk-list',
  templateUrl: './patient-risk-list.component.html',
  styleUrls: ['./patient-risk-list.component.scss']
})
export class PatientRiskListComponent extends BaseComponent implements OnInit {
  @Input() actions: any[] = [];
  @Input() heading: string = 'RISK MANAGEMENT';
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  /** Grouped and filtered. `null` means "still loading" and drives `#listLoading`. */
  listData!: any[] | null;
  patient: any;

  /**
   * L2 — the D7 taxonomy, BY IMPORT. The closed category list is never restated here: a second
   * copy is a list that can be relaxed in one place and not the other, which is the whole reason
   * D7 chose a closed taxonomy over free text.
   */
  readonly categoryOptions: readonly string[] = RISK_CATEGORIES;
  categoryFilter: string = '';

  /** Same reasoning as the categories above: the severity vocabulary arrives by import.
   *  Status is a plain string set (D9) and has no shared helper to import. */
  readonly severityOptions: readonly string[] = RISK_SEVERITIES;
  // D9, from the one definition in `risk-suggestion.model.ts` rather than a fourth copy of the
  // same four strings — the same rule `RISK_CATEGORIES` above already follows.
  readonly statusOptions: readonly string[] = RISK_STATUSES;
  severityFilter: string = '';
  statusFilter: string = '';

  /** Everything for this patient, unfiltered — so clearing the filter needs no refetch. */
  private allItems: any[] | null = null;

  // THE `hospitals` / `admissionRecords` / `appointments` FEEDS ARE GONE, with the three services
  // that supplied them. They existed only to build the §4 facility preview for the add dialog this
  // section used to open (L5/D13). Workstream N moved both the add and the edit onto
  // `patient-risk-detail`, which reads those same root-scoped feeds itself, so keeping three
  // subscriptions alive here to compute a preview nothing renders would be dead weight.

  constructor(
    private patientRiskItemService: PatientRiskItemService,
    private patientService: PatientService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.loadRiskData();
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  loadRiskData(): void {
    // J1's patient-scoped service. A non-patient facility issue (`patientId` NULL, §5c) never
    // reaches this stream at all, so "items with no patient stay out of every patient's file" is
    // structural here rather than a rule this component has to remember.
    this.patientRiskItemService.patientRiskItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.allItems = items;
          this.applyFilter();
        },
        error: (err) => {
          console.error('Error loading risk items:', err);
          this.allItems = [];
          this.applyFilter();
        }
      });

    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(patient => {
        this.patient = patient;
      });
  }

  /** True once anything has loaded — gates the filter row so an empty section stays quiet. */
  get hasAnyItems(): boolean {
    return !!(this.allItems && this.allItems.length);
  }

  get isFiltered(): boolean {
    return !!(this.categoryFilter || this.severityFilter || this.statusFilter);
  }

  /**
   * Shown on the header icon. A filter menu that is closed must not be able to hide the fact
   * that the list is filtered - the same reason the register carries a count on its trigger.
   */
  get activeFilterCount(): number {
    return [this.categoryFilter, this.severityFilter, this.statusFilter].filter(v => !!v).length;
  }

  /** Keeps Tab inside the open menu instead of letting it close. */
  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  /** L2 — re-groups from `allItems`; the filter is a view, never a refetch. */
  applyFilter(): void {
    if (!this.allItems) {
      this.listData = null;
      return;
    }

    const category = String(this.categoryFilter || '').toLowerCase();
    const severity = String(this.severityFilter || '').toLowerCase();
    const status = this.statusFilter;

    const filtered = this.allItems.filter(item => {
      if (category && String(item?.category || '').toLowerCase() !== category) return false;
      if (severity && String(item?.severity || '').toLowerCase() !== severity) return false;
      if (status && item?.status !== status) return false;
      return true;
    });

    this.listData = this.sortRiskRecords(filtered);
  }

  clearFilter(): void {
    this.categoryFilter = '';
    this.severityFilter = '';
    this.statusFilter = '';
    this.applyFilter();
  }

  /**
   * The calendar day this item files under. See the class header for why this is `dateOfIssue`
   * and why it is read off the string.
   */
  private riskGroupKey(item: any): string {
    const dateOfIssue = item?.dateOfIssue;
    if (dateOfIssue) {
      const day = String(dateOfIssue).match(/^\d{4}-\d{2}-\d{2}/);
      if (day) return day[0];
      return moment(dateOfIssue).format('YYYY-MM-DD');
    }
    // `dateOfIssue` is NOT NULL in the schema, so this is defence against a partial projection
    // rather than an expected shape. A timestamp is a real local instant, so it goes through
    // moment.
    return item?.createdAt ? moment(item.createdAt).format('YYYY-MM-DD') : '';
  }

  sortRiskRecords(items: any[]): any[] {
    const grouped = _.groupBy(items || [], item => this.riskGroupKey(item));

    return _.chain(grouped)
      .map((value, key) => ({ date: key, items: value }))
      // ISO day strings sort lexicographically, which is why the key is kept in that form.
      .orderBy(group => group.date, 'desc')
      .value();
  }

  // ── Display labels, all by import (D6/D7/D9). ──────────────────────────────────────────

  categoryLabel(category?: string): string {
    return riskCategoryLabel(category);
  }

  severityLabel(severity?: string): string {
    return riskSeverityLabel(severity);
  }

  /** "Consumables · High" — the row's one-line classification. */
  riskTitle(item: any): string {
    return [riskCategoryLabel(item?.category), riskSeverityLabel(item?.severity)]
      .filter(Boolean)
      .join(' · ');
  }

  /**
   * The facility this issue is filed against.
   *
   * Reads the `hospitalName` SNAPSHOT (D5) and never a resolved `hospitalId`: §4 branch 3 matches
   * an admission-history name against `Hospitals` and misses legitimately and often ('Olivedale'
   * vs 'Netcare Olivedale'), leaving a NULL id beside a perfectly good name. With no facility
   * named at all the D6 `location` IS the answer — `rooms` reads as In-rooms / OPD, a first-class
   * outcome, never as "unknown".
   */
  riskFacility(item: any): string {
    const named =
      item?.hospitalName || item?.wardName
        ? `${item?.hospitalName || ''} (${item?.wardName || ''})`.replace(/\(\s*\)/, '').trim()
        : '';
    return named || riskLocationLabel(item?.location);
  }

  handleActionClick(event: MouseEvent, action: any) {
    event.preventDefault();
    event.stopPropagation();
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    action.action(event);
  }

  /**
   * N4 — THE EDIT HALF, ROUTED.
   *
   * Was: open G's `risk-item-detail-dialog` read-only. Now it is the same hand-off the task list
   * has always used — `setEditingRiskItem(item)` and THEN navigate — so a row in the patient file
   * opens the same editable page the `+` opens, which is what §11 asked for ("allow these items to
   * be edited from in the patient file, same as timeline notes").
   *
   * The order matters and is not incidental: `patient-risk-detail` reads the row off
   * `editingRiskItem$` as it initialises, and this component is DESTROYED by the navigation
   * because both live in the `patient` outlet. Setting after navigating would set it on a
   * component that is already gone.
   */
  loadItem(item: any) {
    if (!item || !item.id) return;
    if (this.patientService.isRecordingLocked()) {
      return;
    }

    this.patientRiskItemService.setEditingRiskItem(item);
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-risk-item', item.id],
      }
    }]);
  }

  // `openAddRiskItem` AND `describeRiskResolution` USED TO SIT HERE and are gone.
  //
  // They were L5/D13's manual-add path: open the shared confirm-before-add dialog with no
  // suggestion, POST the result, then describe what the server filed it against. Workstream N
  // replaced that with the `add-risk-item` PAGE, and `patient-file.component.ts`'s `manageRiskItem`
  // — the only caller either ever had — now routes there directly. Leaving a second, unreachable
  // add path in place would be a copy of the D7 taxonomy, the D8 evidence wording and the §4
  // preview that nothing exercises and that could quietly drift from the page that is real.
  //
  // THE DIALOG ITSELF IS UNTOUCHED AND STILL IN USE: it remains the AI-suggestion accept path in
  // `patient-ai-request-detail` and `patient-note-detail` (§11, confirmed). Only this section's
  // call into it is gone.
}
